import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  AddEmployeesToAttendancePolicyDocument,
  AttendancePolicyEmployeesCollectionListDocument,
  AttendancePolicySelectDocument,
  AttendancePolicySelectFragment,
  AttendancePolicySelectQuery,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import ModalComponent from 'modules/core/components/Modal'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'
import app from 'modules/core/store/models/app'

import attendancePolicies from 'modules/attendance/store/collections/policies'
import type AttendancePolicy from 'modules/attendance/store/models/AttendancePolicy'

interface Props {
  attendancePolicy: AttendancePolicy
  onClose: () => void
  employeeData: {
    id: string
    name: string
  }
}

interface ChangePolicyFormValues {
  policy: AttendancePolicySelectFragment | null
}

const AttendancePolicyItem = ({ policy }: { policy: AttendancePolicySelectFragment }) => {
  return (
    <Box gap="s2">
      <Text.Normal weight="medium" color="grey1000" ellipsis>
        {policy.name}
      </Text.Normal>
    </Box>
  )
}

const TransferToPolicyModal = ({ employeeData, onClose, attendancePolicy }: Props) => {
  const [addEmployeesToAttendancePolicy, { loading }] = useMutation(
    AddEmployeesToAttendancePolicyDocument,
    {
      extractorFn: (data) => data.attendanceMutations.addEmployeesAttendanceEmployeesPolicy,
      refetchQueries: [AttendancePolicyEmployeesCollectionListDocument],
    }
  )

  const form = useForm<ChangePolicyFormValues>({
    defaultValues: {
      policy: null,
    },
    validate: (data) => {
      const errors: FormErrors<ChangePolicyFormValues> = {}

      if (!data.policy) {
        errors.policy = i18n.t('common.validations.empty')
      }
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await addEmployeesToAttendancePolicy({
        variables: {
          policyId: data.policy?.id ?? '0',
          employeeIds: [employeeData.id],
        },
      })
      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success && handled.data?.employeesPolicy?.employeeIds) {
        onClose()

        await attendancePolicies.fetch()
        app.setFlash({
          type: 'correct',
          message: i18n.t('app_store_v2.apps.time_tracking.transfer_modal.update_success', {
            employee_name: employeeData.name,
            policy_name: form.values.policy?.name,
          }),
          time: 2000,
        })
      }
      return handled
    },
  })

  const extractConnection = (data: AttendancePolicySelectQuery) => {
    return data.attendance.employeesPoliciesConnection
  }

  const renderEntity = (entity: AttendancePolicySelectFragment) => {
    return <AttendancePolicyItem policy={entity} />
  }

  const filterPolicies = (entities: AttendancePolicySelectFragment[], search: string) => {
    if (!search.trim()) {
      return entities
    }
    return entities.filter((policy) => matchText(search, policy.name))
  }

  return (
    <ModalComponent
      illustration="calendar-clock"
      title={i18n.t('app_store_v2.apps.time_tracking.transfer_modal.title', {
        name: employeeData.name,
      })}
      description={i18n.t('app_store_v2.apps.time_tracking.transfer_modal.description')}
      onSubmit={() => form.handleSubmit()}
      onClose={onClose}
      footer={
        <Button disabled={loading} submit>
          {i18n.t('app_store_v2.apps.time_tracking.transfer_modal.button')}
        </Button>
      }
    >
      <EntitySelector
        document={AttendancePolicySelectDocument}
        extractConnection={extractConnection}
        renderEntity={renderEntity}
        renderValue={(entity) =>
          entity !== null ? (
            <Text.Normal>{entity.name}</Text.Normal>
          ) : (
            <Text.Normal>{i18n.t('common.select')}</Text.Normal>
          )
        }
        value={form.values.policy}
        variables={{
          excludeIds: [attendancePolicy.id],
        }}
        filterEntitiesBySearch={filterPolicies}
        onChange={(item) => {
          form.setValues({ policy: item ?? null })
        }}
      />
    </ModalComponent>
  )
}

export default observer(TransferToPolicyModal)
